import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Leaf, LogOut, Mail, Phone, Calendar, Users, Vote } from 'lucide-react';
import { toast } from 'sonner';

interface MovementSubmission {
  id: string;
  created_at: string;
  interest_type: string;
  masked_email: string;
  masked_phone: string;
  first_name: string;
  has_description: boolean;
}

interface CommunityVote {
  id: string;
  created_at: string;
  session_id: string;
  event_preference: string;
  experience_categories: string[];
  masked_email: string;
  has_email: boolean;
}

interface AuditLog {
  id: string;
  created_at: string;
  action: string;
  table_name: string;
  admin_user_id: string;
}

export default function Admin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [submissions, setSubmissions] = useState<MovementSubmission[]>([]);
  const [votes, setVotes] = useState<CommunityVote[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      navigate('/auth');
      return;
    }

    // Check if user is admin
    const { data: roleData, error: roleError } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', session.user.id)
      .eq('role', 'admin')
      .single();

    if (roleError || !roleData) {
      toast.error('Access denied. Admin privileges required.');
      await supabase.auth.signOut();
      navigate('/auth');
      return;
    }

    setIsAdmin(true);
    loadData();
  };

  const loadData = async () => {
    setLoading(true);
    
    // Load masked movement submissions
    const { data: submissionData } = await supabase
      .rpc('get_audited_movement_submissions');
    
    if (submissionData) {
      setSubmissions(submissionData);
    }

    // Load masked community votes
    const { data: voteData } = await supabase
      .rpc('get_masked_community_votes');
    
    if (voteData) {
      setVotes(voteData);
    }

    // Load audit logs
    const { data: logData } = await supabase
      .from('admin_audit_log')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(50);
    
    if (logData) {
      setAuditLogs(logData);
    }

    setLoading(false);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    toast.success('Signed out successfully');
    navigate('/auth');
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-nature-earth p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Leaf className="w-10 h-10 text-accent" strokeWidth={1.5} />
            <div>
              <h1 className="text-4xl font-serif font-bold text-nature-stone">Mystic Forest Admin</h1>
              <p className="text-muted-foreground">Protected Data Management Portal</p>
            </div>
          </div>
          <Button onClick={handleSignOut} variant="outline">
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>

        {loading ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-muted-foreground">Loading data...</p>
            </CardContent>
          </Card>
        ) : (
          <Tabs defaultValue="submissions" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="submissions">
                <Users className="w-4 h-4 mr-2" />
                Movement Submissions ({submissions.length})
              </TabsTrigger>
              <TabsTrigger value="votes">
                <Vote className="w-4 h-4 mr-2" />
                Community Votes ({votes.length})
              </TabsTrigger>
              <TabsTrigger value="audit">
                <Calendar className="w-4 h-4 mr-2" />
                Audit Logs ({auditLogs.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="submissions">
              <Card>
                <CardHeader>
                  <CardTitle>Movement Submissions (Masked PII)</CardTitle>
                  <CardDescription>
                    User data is masked to protect privacy. Contact details are partially hidden.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Interest Type</TableHead>
                        <TableHead>Email (Masked)</TableHead>
                        <TableHead>Phone (Masked)</TableHead>
                        <TableHead>Description</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {submissions.map((sub) => (
                        <TableRow key={sub.id}>
                          <TableCell className="text-sm">
                            {new Date(sub.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>{sub.first_name || 'N/A'}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{sub.interest_type}</Badge>
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            <Mail className="w-3 h-3 inline mr-1" />
                            {sub.masked_email}
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            <Phone className="w-3 h-3 inline mr-1" />
                            {sub.masked_phone || 'N/A'}
                          </TableCell>
                          <TableCell>
                            {sub.has_description ? '✓ Yes' : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="votes">
              <Card>
                <CardHeader>
                  <CardTitle>Community Votes (Masked PII)</CardTitle>
                  <CardDescription>
                    Voting data with email addresses partially masked for privacy.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Event Preference</TableHead>
                        <TableHead>Experience Categories</TableHead>
                        <TableHead>Email (Masked)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {votes.map((vote) => (
                        <TableRow key={vote.id}>
                          <TableCell className="text-sm">
                            {new Date(vote.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Badge>{vote.event_preference || 'N/A'}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {vote.experience_categories.map((cat, i) => (
                                <Badge key={i} variant="secondary" className="text-xs">
                                  {cat}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {vote.has_email ? (
                              <>
                                <Mail className="w-3 h-3 inline mr-1" />
                                {vote.masked_email}
                              </>
                            ) : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="audit">
              <Card>
                <CardHeader>
                  <CardTitle>Admin Audit Logs</CardTitle>
                  <CardDescription>
                    All admin access to sensitive data is logged here.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Timestamp</TableHead>
                        <TableHead>Action</TableHead>
                        <TableHead>Table</TableHead>
                        <TableHead>Admin User ID</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {auditLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell className="text-sm">
                            {new Date(log.created_at).toLocaleString()}
                          </TableCell>
                          <TableCell className="font-mono text-sm">{log.action}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{log.table_name}</Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {log.admin_user_id.slice(0, 8)}...
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
